
import Container from "./Container";
import parser from "./parser";
import AST from "./AST";

const rangeNode = function(start, end) {
    return {
        "start": start,
        "end": end
    };
};

const charNode = function(ch) {
    return rangeNode(ch, ch);
};

const classNode = function(ranges, reversed) {
    return new AST.Value("regexp:class", {
        "reversed": Boolean(reversed),
        "ranges": ranges
    });
};

const specialNumber = [
    rangeNode("0", "9")
];

const specialWord = [
    rangeNode("0", "9"),
    rangeNode("a", "z"),
    rangeNode("A", "Z"),
    charNode("_")
];

const specialSpace = [
    charNode("\u0009"),
    rangeNode("\u000a", "\u000d"),
    charNode("\u0020​"),
    charNode("\u00a0"),
    charNode("\u1680​"),
    charNode("\u180e"),
    rangeNode("\u2000​", "\u200a​"),
    rangeNode("\u2028", "\u2029​​"),
    charNode("\u202f"),
    charNode("\u205f​"),
    charNode("\u3000")
];

const specials = new Container({
    "d": classNode(specialNumber),
    "D": classNode(specialNumber, true),
    "w": classNode(specialWord),
    "W": classNode(specialWord, true),
    "s": classNode(specialSpace),
    "S": classNode(specialSpace, true)
});

const word = parser.word;
const union = parser.union;
const sequence = parser.sequence;
const repeat = parser.repeat;
const proxy = parser.proxy;
const epsilon = parser.epsilon;

const or = proxy();

const parens = sequence(word("("), or, word(")")).pick(1).map(function(match) {
    return new AST.Unary("regexp:group", match);
});

const charClassChar = word(/\\u([0-9a-fA-F]{4})|\\([^u])|([^\\\-\^\[\]])/).map(function(match) {
    if (match[1]) {
        return String.fromCharCode(parseInt(match[1], 16));
    } else if (match[2]) {
        return match[2];
    } else {
        return match[3];
    }
});

const charClassRange = sequence(charClassChar, word("-"), charClassChar).pick(0, 2).transform(function(result) {
    const start = result.match[0];
    const end = result.match[1];
    if (start.charCodeAt(0) > end.charCodeAt(0)) {
        return null;
    }
    return result;
});

const charClass = sequence(
    word("["),
    word(/(\^?)/).map(function(match) {
        return Boolean(match[1]);
    }),
    repeat(
        union(
            charClassRange.map(function(match) {
                return rangeNode(match[0], match[1]);
            }),
            charClassChar.map(function(ch) {
                return charNode(ch);
            })
        ),
        1
    ),
    word("]")
).pick(1, 2).map(function(match) {
    const reversed = match[0];
    const ranges = match[1];
    return classNode(ranges, reversed);
});

const chars = word(/\\u([0-9a-fA-F]{4})|\\([^u])|([^\/\[\]\\\(\)\.\?\*\+\{\}\^\$\|])/).map(function(match) {
    if (match[1]) {
        return new AST.Value("regexp:char", {
            "char": String.fromCharCode(parseInt(match[1], 16))
        });
    } else if (match[2]) {
        return specials.get(match[2]) || new AST.Value("regexp:char", {
            "char": match[2]
        });
    } else {
        return new AST.Value("regexp:char", {
            "char": match[3]
        });
    }
});

const anychar = word(".").map(function() {
    return new AST.Value("regexp:anychar");
});

const start = word("^").map(function() {
    return new AST.Value("regexp:start");
});

const end = word("$").map(function() {
    return new AST.Value("regexp:end");
});

const rep = sequence(
    union(charClass, chars, anychar, parens),
    union(
        word("*").map(function() {
            return {"min": 0, "max": Infinity};
        }),
        word("+").map(function() {
            return {"min": 1, "max": Infinity};
        }),
        word("?").map(function() {
            return {"min": 0, "max": 1};
        }),
        word(/\{(\d+)\}/).map(function(match) {
            const count = Number(match[1]);
            return {"min": count, "max": count};
        }),
        word(/\{(\d+),(\d+)\}/).transform(function(result) {
            const min = Number(result.match[1]);
            const max = Number(result.match[2]);
            if (min > max) {
                return null;
            }
            return {
                "match": {"min": min, "max": max},
                "tail": result.tail
            };
        }),
        epsilon(null)
    )
).map(function(match) {
    const child = match[0];
    const range = match[1];

    if (range === null) {
        return child;
    } else {
        return new AST.Unary("regexp:repeat", child, {
            "min": range.min,
            "max": range.max
        });
    }
});

const seq = repeat(
    union(
        start,
        end,
        rep
    )
).map(function(matches) {
    if (matches.length === 0) {
        return new AST.Value("regexp:empty");
    } else if (matches.length === 1) {
        return matches[0];
    } else {
        return new AST.NAry("regexp:sequence", matches);
    }
});

or.set(
    sequence(
        seq,
        repeat(
            sequence(
                word("|"),
                seq
            ).pick(1)
        )
    ).map(function(match) {
        if (match[1].length === 0) {
            return match[0];
        }
        return new AST.NAry("regexp:or", [match[0]].concat(match[1]));
    })
);

export default sequence(word("/"), or, word("/"), word(/i?/).pick(0)).pick(1, 3).map(function(match) {
    return {
        "ast": match[0],
        "ignoreCase": match[1] === "i"
    };
});



// WEBPACK FOOTER //
// ./src/abusesa-js/regexpAST.js